import { camelCase, isNil, isPlainObject, isString, snakeCase } from 'lodash';
import { LocalStorage } from '../storage';
import { isTimezoneValid } from '../validation';
// MOVE TO CONSTANTS
export const urlViews = {
  URL: 'url',
  TITLE: 'title',
};
// MOVE TO CONSTANTS
export const pageType = {
  STATIC: 'static',
  DYNAMIC: 'dynamic',
};

export function getParameterByName(name: string, url?: string) {
  const href = url ?? window.location.href;
  let searchParams: URLSearchParams;
  try {
    searchParams = new URL(href).searchParams;
  } catch {
    // Handle relative URLs
    searchParams = new URL(href, window.location.origin).searchParams;
  }

  // Support both camelCase and snake_case params (for backward compatibility). The usage of snake_case is discouraged - always use camelCase.
  const variants = Array.from(new Set([camelCase(name), snakeCase(name)]));
  for (const variant of variants) {
    const value = searchParams.get(variant);
    if (value !== null) return value === '' ? null : value;
  }

  return null;
}

/**
 * Get a URL parameter by name, and save it to localStorage.
 *
 * If the parameter is not found in the URL, it will check localStorage for a fallback value.
 */
export function getUrlParameterWithStorageFallback(name: string, storageItemName: string, url?: string) {
  let value = getParameterByName(name, url);

  if (!isString(value)) {
    const storageValue = LocalStorage.getItem(storageItemName);

    if (!isString(storageValue)) return null;

    value = storageValue;
  } else {
    LocalStorage.setItem(storageItemName, value);
  }

  return value;
}

export const formatQueryParamsToRedirectURL = () => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  const pushTo = searchParams.get('pushTo');

  if (pushTo) searchParams.delete('pushTo');

  const queryString = searchParams.toString();
  return `${pushTo ?? ''}${queryString ? `?${queryString}` : ''}`;
};

export const updateUrlParameter = (key: string, value: string | string[]) => {
  const url = new URL(window.location.href);
  const hash = window.location.hash;
  const searchParams = new URLSearchParams(url.search);
  const canonicalKey = camelCase(key);

  Array.from(new Set([snakeCase(key), canonicalKey])).forEach((variant) => searchParams.delete(variant));

  if (Array.isArray(value)) {
    value.forEach((val) => searchParams.append(canonicalKey, val));
  } else {
    searchParams.set(canonicalKey, value);
  }
  const updatedUrl = `${url.pathname}?${searchParams.toString()}${hash}`;
  return updatedUrl;
};

export const removeUrlParameter = (keys: string | string[]) => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  const hash = window.location.hash;

  const normalizedKeys = (typeof keys === 'string' ? [keys] : keys).flatMap((key) =>
    Array.from(new Set([snakeCase(key), camelCase(key)])),
  );
  normalizedKeys.forEach((key) => searchParams.delete(key));

  const updatedUrl = `${url.pathname}?${searchParams.toString()}${hash}`;
  return updatedUrl;
};

export function getUrlWithTrailingSlash(url: string) {
  return url && url.replace(/\/?$/, '/');
}

/**
 * Prepends the websiteUrl to a relative path (if it's not already prepended)
 */
export function getDynamicPageUrl(pattern: string, websiteUrl: string) {
  const websiteDomain = formatWebsitePage(websiteUrl).split('/')[0].toLowerCase();
  if (websiteDomain && pattern.toLowerCase().includes(websiteDomain)) return pattern;
  if (/^\**https?:\/\//i.test(pattern)) return pattern;
  return `${getUrlWithTrailingSlash(websiteUrl)}${pattern}`;
}

export const dynamicPageUrlErrors = {
  EMPTY: 'empty',
  PROTOCOL: 'protocol',
  WWW: 'www',
  SPACE: 'space',
  FORBIDDEN_CHAR: 'forbiddenChar',
} as const;

export type DynamicPageUrlError = {
  type: (typeof dynamicPageUrlErrors)[keyof typeof dynamicPageUrlErrors];
  char?: string;
};

// Mirrors the pattern validation of website-service, which answers with INVALID_DATA for any of these
const DYNAMIC_PAGE_FORBIDDEN_CHARS = /[*$%^&()+=[\]';,{}|"<>?~]/;

/**
 * Dynamic page patterns are saved relative to the website url and the API rejects absolute urls,
 * so a url pointing to the website itself is reduced to its path. Urls of other domains are kept as they are.
 */
export function getDynamicPageRelativeUrl(url: string, websiteUrl: string) {
  const trimmedUrl = url.trim();
  const websiteDomain = getUrlWithoutProtocolAndTrailingSlash(websiteUrl ?? '').toLowerCase();
  const urlWithoutProtocol = trimmedUrl.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  const loweredUrl = urlWithoutProtocol.toLowerCase();

  if (!websiteDomain || (loweredUrl !== websiteDomain && !loweredUrl.startsWith(`${websiteDomain}/`))) {
    return trimmedUrl;
  }

  return urlWithoutProtocol.slice(websiteDomain.length).replace(/^\/+/, '');
}

export function getDynamicPageRelativeUrlError(url: string): DynamicPageUrlError | null {
  if (!url) return { type: dynamicPageUrlErrors.EMPTY };
  if (/https?:\/\//i.test(url)) return { type: dynamicPageUrlErrors.PROTOCOL };
  if (/www\./i.test(url)) return { type: dynamicPageUrlErrors.WWW };
  if (/\s/.test(url)) return { type: dynamicPageUrlErrors.SPACE };

  const forbiddenChar = url.match(DYNAMIC_PAGE_FORBIDDEN_CHARS)?.[0];
  if (forbiddenChar) return { type: dynamicPageUrlErrors.FORBIDDEN_CHAR, char: forbiddenChar };

  return null;
}

export function getUrlWithoutTrailingSlash(url: string) {
  return url && url.replace(/\/?$/, '');
}

export function getUrlWithoutQueryString(url: string) {
  return url && url.split('?')[0];
}

export function getUrlWithoutProtocolAndTrailingSlash(url: string) {
  return getUrlWithoutTrailingSlash(removeWww(removeProtocol(url)));
}

export function formatWebsitePage(url: string) {
  return removeWww(removeProtocol(url));
}

export function removeWww(url: string) {
  if (getWellFormedUrl(url).includes('//www.')) return url.replace('www.', '');
  return url;
}

export function getUrlPrefix(url: string) {
  const urlWithoutDomain = url.split('.')[0];
  return url && removeProtocol(urlWithoutDomain);
}

export function removeProtocol(url: string) {
  if (url.includes('http://')) return url.replace('http://', '');
  if (url.includes('https://')) return url.replace('https://', '');

  return url;
}

export function getWellFormedUrl(url: string) {
  const protocolPattern = new RegExp(/^(?:(?:https|http):\/\/)/);
  const hasProtocol = url && protocolPattern.test(url.toString().toLowerCase());
  return hasProtocol ? url : 'https://' + url;
}

export const toQueryString = function (obj: Record<string, any> | undefined, prefix?: string): string {
  if (!obj) return '';
  // eslint-disable-next-line prefer-const
  let str = [],
    p;
  for (p in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(p) && !isNil(obj[p])) {
      const k = prefix ? prefix + '[' + p + ']' : p,
        v = obj[p];
      str.push(
        v !== null && isPlainObject(v) ? toQueryString(v, k) : encodeURIComponent(k) + '=' + encodeURIComponent(v),
      );
    }
  }

  return str.join('&');
};

export const addProtocol = (url: string, websiteBaseUrl: string | null, pageTypeData: string | null, ssl = false) => {
  if (!url) return '';
  if (pageType.DYNAMIC === pageTypeData && websiteBaseUrl) {
    url = url.includes(websiteBaseUrl) ? url : websiteBaseUrl + url;
  }

  if (!url.includes('://')) {
    return `http${ssl ? 's' : ''}://` + url;
  }
  return url;
};

export function withProtocol(url: string, ssl = true) {
  if (!/^https?:\/\//i.test(url)) {
    return `http${ssl ? 's' : ''}://${url}`;
  }
  return url;
}

export const getUrlView = (websiteUrlView: string, url: string, pageName: string) => {
  if (websiteUrlView === urlViews.TITLE || !url) return pageName;

  try {
    const urlInfo = new URL(addProtocol(url, null, null, true));
    return urlInfo.pathname === '/' ? urlInfo.host : urlInfo.pathname;
  } catch {
    return url;
  }
};

export function getUrlPathname(url: string) {
  try {
    const fullUrl = new URL(url);
    return fullUrl.pathname;
  } catch (e) {
    console.log(`Cannot construct URL from ${url}`);
    return '/';
  }
}

export function getTimezoneFromQueryParams() {
  const timezone = getParameterByName('timezone') ?? getTimezoneFromEssentialsObject();

  if (!timezone || !isTimezoneValid(timezone)) return null;

  return timezone;
}

/**
 * WiX might add the timezone to an **essentials** object
 */
const getTimezoneFromEssentialsObject: () => string | null = () => {
  const currentUrl = new URL(window.location.href);
  const essentials = currentUrl.searchParams.get('essentials');
  if (!essentials) return null;
  try {
    const parsedEssentials = JSON.parse(decodeURIComponent(essentials));
    return parsedEssentials?.timezone || null;
  } catch (e) {
    return null;
  }
};

export function getUrlOrigin(url: string) {
  try {
    const fullUrl = new URL(url);
    return fullUrl.origin;
  } catch (e) {
    console.log(`Cannot construct URL from ${url}`);
    return '';
  }
}
